import React, { useEffect, useState } from "react";
import { Box, Typography, Divider, Collapse, Grid } from "@mui/material";
import ReadMoreButton from "../../shared/components/ReadMoreButton";
import { useParams } from "react-router-dom";
import { apiRequest } from "../../shared/api/apiClient";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import IconButton from "@mui/material/IconButton";
import { groupToArrayBySampleId } from "../../shared/General/GeneralFuntions";
import RGIResultsTable from "../../shared/components/RGItable";
import LoadingSpinner from "../../shared/components/LoadingSpinner";

export default function AnalyzeResultsResistanceRgi() {
    const { id } = useParams();
    const token = process.env.REACT_APP_ATLAS_TOKEN;

    const [rgiData, setRgiData] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            if (!id || !token) return;

            setLoading(true);

            try {
                const params = new URLSearchParams({
                    run_id: id,   // 👈 aquí usas run_id (según tu comentario original)
                    token,
                });

                const endpoint = `rgi.php?${params.toString()}`;

                const data = await apiRequest(endpoint, {
                    method: "GET",
                });

                if (!cancelled) {
                    const samples = groupToArrayBySampleId(data?.rows ?? []);
                    setRgiData(samples);
                }

            } catch (e) {
                if (!cancelled) console.error(e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();

        return () => {
            cancelled = true;
        };

    }, [id, token]);
    return (
        <Box
            sx={{
                width: "100%",
                mx: "auto",
                px: 4, // desktop-only (igual que Resfinder)
            }}
        >
            {/* Title block */}
            <Box
                sx={{
                    width: "100%",
                    mb: 6,
                }}
            >
                <Typography variant="h4">Resistance genes</Typography>

                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 1 }}>
                    <Box
                        sx={{
                            width: 14,
                            height: 28,
                            borderTopRightRadius: 999,
                            borderBottomRightRadius: 999,
                            background: "#20BC71",
                            flexShrink: 0,
                        }}
                    />
                    <Typography variant="h5">rgi</Typography>
                </Box>
            </Box>

            <Grid
                container
                spacing={3}
                sx={{
                    width: "100%",
                    mb: 10,
                    display: "flex",
                    justifyContent: "flex-start",
                }}
            >
                {/* Left column */}
                <Grid item xs={12} md={6}>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            flexDirection: "column",
                            height: "100%",
                            width: "100%",
                        }}
                    >
                        <Box sx={{ mb: 5 }}>
                            <Typography variant="body1">
                                These results were generated by The Resistance Gene Identifier (RGI) software.
                            </Typography>
                            <Typography variant="body1">
                                To read the documentation please visit the oficial website
                            </Typography>
                        </Box>

                        <ReadMoreButton href={"https://github.com/arpcard/rgi"} />
                    </Box>
                </Grid>

                {/* Right column */}
                <Grid
                    item
                    xs={12}
                    md={6}
                    sx={{
                        display: "flex",
                        justifyContent: { xs: "flex-start", md: "flex-end" },
                    }}
                >
                    <Box
                        sx={{
                            background: "#000",
                            color: "#fff",
                            width: "100%",
                            maxWidth: 460,
                            minWidth: { xs: "auto", md: 400 },
                            minHeight: 144,
                            borderRadius: 4,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            p: 2,
                        }}
                    >
                        <Box
                            sx={{
                                textAlign: "left",
                                wordBreak: "break-word",
                                fontFamily:
                                    "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
                            }}
                        >
                            <Typography variant="body2">{`rgi main \\`}</Typography>
                            <Typography variant="body2">{`--input_sequence \${spades_scaffold_bacterial} \\`}</Typography>
                            <Typography variant="body2">{`--output_file virulence_rgi/\${sample}/results \\`}</Typography>
                            <Typography variant="body2">{`--local --low_quality`}</Typography>
                        </Box>
                    </Box>
                </Grid>
            </Grid>

            {/* Loading / Samples */}
            {loading ? (
                <Box
                    sx={{
                        width: "100%",
                        display: "grid",
                        placeItems: "center",
                        mt: 6,
                        gap: 2,
                    }}
                >
                    <LoadingSpinner size={170} barWidth={18} barHeight={48} />
                    <Typography sx={{ fontSize: 40 }}>Processing files</Typography>
                </Box>
            ) : (
                rgiData?.map((sample, key) => <RgiSample sample={sample} key={key} />)
            )}
        </Box>
    );
}

const RgiSample = ({ sample }) => {
    const [openSample, setOpenSample] = useState(false);

    return (
        <Box sx={{ width: "100%" }}>
            {/* Sample header (clickeable toda la fila, igual que Resfinder) */}
            <Box
                sx={{
                    width: "100%",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    py: 1,
                    cursor: "pointer",
                }}
                onClick={() => setOpenSample((v) => !v)}
            >
                <Box sx={{ width: "100%" }}>
                    <Typography variant="h6">Sample {sample?.sample_id}</Typography>
                </Box>

                <Box sx={{ width: "10%", display: "flex", flexDirection: "row", justifyContent: "end" }}>
                    <IconButton
                        sx={{
                            maxWidth: '50px',
                            width: "40px",
                            aspectRatio: '1/1',
                            border: "1px solid",
                            borderColor: "divider",
                            flexShrink: 0,
                        }}

                        aria-label={openSample ? "Collapse" : "Expand"}
                    >
                        {openSample ? (
                            <ExpandLessRoundedIcon sx={{ width: "20px", height: "20px" }} />
                        ) : (
                            <ExpandMoreRoundedIcon sx={{ width: "20px", height: "20px" }} />
                        )}
                    </IconButton>
                </Box>
            </Box>

            <Collapse in={openSample} timeout={240} unmountOnExit sx={{ width: '100%' }}>
                <Divider sx={{ borderColor: "#20BC71" }} />

                <Box sx={{ pb: 3, pt: 3, width: '100%' }} >
                    <RGIResultsTable rows={sample?.rows ?? []} />
                </Box>
            </Collapse>

            <Divider sx={{ borderColor: "#20BC71" }} />
        </Box>
    );
};